import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Space,
  Tag,
  Progress,
  Alert,
  Tabs,
  List,
  Avatar,
  Typography,
  Badge,
  Tooltip,
  Select,
  DatePicker,
  Input,
  Timeline,
  Divider,
} from 'antd';
import {
  ShoppingCartOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  TruckOutlined,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  SettingOutlined,
  BellOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { 
  PurchaseRequisition,
  PurchaseOrder,
  Vendor,
  VendorInvoice,
  ApprovalRecord,
  Priority,
  RequisitionStatus,
  PurchaseOrderStatus,
  ApprovalStatus 
} from '../../types';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

interface ProcurementDashboardProps {
  onCreateRequisition: () => void;
  onCreatePurchaseOrder: () => void;
  onManageVendors: () => void;
  onManageWorkflows: () => void;
}

export const ProcurementDashboard: React.FC<ProcurementDashboardProps> = ({
  onCreateRequisition,
  onCreatePurchaseOrder,
  onManageVendors,
  onManageWorkflows,
}) => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('month');

  // Mock data - replace with actual API calls
  const mockDashboardData = {
    totalSpend: 2456789.50,
    totalRequisitions: 156,
    pendingApprovals: 23,
    activeVendors: 45,
    avgApprovalTime: 2.5,
    costSavings: 125000,
    onTimeDelivery: 92.5,
    budgetUtilization: 78.3,
    recentRequisitions: [
      {
        id: '1',
        requisitionNumber: 'PR-2024-001',
        title: 'Office Supplies Q1',
        requestedBy: 'John Doe',
        department: 'Administration',
        totalAmount: 5500,
        status: 'pending_approval',
        priority: 'normal',
        createdAt: new Date(),
      },
      {
        id: '2',
        requisitionNumber: 'PR-2024-002',
        title: 'IT Equipment Upgrade',
        requestedBy: 'Jane Smith',
        department: 'IT',
        totalAmount: 25000,
        status: 'approved',
        priority: 'high',
        createdAt: new Date(),
      },
    ],
    pendingApprovalsList: [
      {
        id: '1',
        documentType: 'purchase_requisition',
        documentNumber: 'PR-2024-003',
        title: 'Marketing Materials',
        amount: 8500,
        requestedBy: 'Mike Johnson',
        daysWaiting: 2,
        currentApprover: 'Sarah Wilson',
        priority: 'urgent',
      },
      {
        id: '2',
        documentType: 'purchase_order',
        documentNumber: 'PO-2024-015',
        title: 'Raw Materials',
        amount: 45000,
        requestedBy: 'David Brown',
        daysWaiting: 1,
        currentApprover: 'Robert Davis',
        priority: 'high',
      },
    ],
    recentPurchaseOrders: [
      {
        id: '1',
        orderNumber: 'PO-2024-014',
        vendor: 'ABC Supplies Inc.',
        totalAmount: 15000,
        status: 'approved',
        expectedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        createdAt: new Date(),
      },
      {
        id: '2',
        orderNumber: 'PO-2024-013',
        vendor: 'Tech Solutions Ltd.',
        totalAmount: 32000,
        status: 'received',
        expectedDelivery: new Date(),
        createdAt: new Date(),
      },
    ],
    topVendors: [
      {
        id: '1',
        name: 'ABC Supplies Inc.',
        totalSpend: 125000,
        orderCount: 15,
        rating: 4.5,
        onTimeDelivery: 95,
      },
      {
        id: '2',
        name: 'Tech Solutions Ltd.',
        totalSpend: 98000,
        orderCount: 12,
        rating: 4.2,
        onTimeDelivery: 88,
      },
    ],
    approvalMetrics: [
      {
        step: 'Department Manager',
        avgTime: 1.2,
        pendingCount: 8,
        approvalRate: 95,
      },
      {
        step: 'Finance Review',
        avgTime: 2.1,
        pendingCount: 5,
        approvalRate: 92,
      },
      {
        step: 'Executive Approval',
        avgTime: 3.5,
        pendingCount: 3,
        approvalRate: 98,
      },
    ],
    budgetStatus: [
      {
        department: 'IT',
        budget: 100000,
        spent: 65000,
        committed: 20000,
        available: 15000,
      },
      {
        department: 'Marketing',
        budget: 75000,
        spent: 45000,
        committed: 15000,
        available: 15000,
      },
      {
        department: 'Operations',
        budget: 150000,
        spent: 120000,
        committed: 25000,
        available: 5000,
      },
    ],
  };

  useEffect(() => {
    setDashboardData(mockDashboardData);
  }, [selectedPeriod]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      submitted: 'blue',
      pending_approval: 'orange',
      approved: 'green',
      rejected: 'red',
      cancelled: 'gray',
      received: 'cyan',
      paid: 'purple',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: Priority) => {
    const colors: Record<Priority, string> = {
      low: 'green',
      normal: 'blue',
      high: 'orange',
      urgent: 'red',
      critical: 'purple',
    };
    return colors[priority] || 'default';
  };

  const requisitionColumns = [
    {
      title: 'Requisition',
      dataIndex: 'requisitionNumber',
      key: 'requisitionNumber',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.title}</Text>
        </div>
      ),
    },
    {
      title: 'Requested By',
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      render: (text: string, record: any) => (
        <div>
          <div>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.department}</Text>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => (
        <Text strong>${amount.toLocaleString()}</Text>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: RequisitionStatus) => (
        <Tag color={getStatusColor(status)}>
          {status.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="link" size="small">View</Button>
          <Button type="link" size="small">Edit</Button>
        </Space>
      ),
    },
  ];

  const approvalColumns = [
    {
      title: 'Document',
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.title}</Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (type: string) => (
        <Tag>{type.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Text strong>${amount.toLocaleString()}</Text>
      ),
    },
    {
      title: 'Waiting',
      dataIndex: 'daysWaiting',
      key: 'daysWaiting',
      render: (days: number) => (
        <div>
          <Text>{days} days</Text>
          {days > 3 && <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginLeft: 4 }} />}
        </div>
      ),
    },
    {
      title: 'Current Approver',
      dataIndex: 'currentApprover',
      key: 'currentApprover',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type="link" size="small">Review</Button>
          <Button type="link" size="small">Escalate</Button>
        </Space>
      ),
    },
  ];

  const budgetColumns = [
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      key: 'budget',
      render: (amount: number) => `$${amount.toLocaleString()}`,
    },
    {
      title: 'Spent',
      dataIndex: 'spent',
      key: 'spent',
      render: (amount: number) => `$${amount.toLocaleString()}`,
    },
    {
      title: 'Committed',
      dataIndex: 'committed',
      key: 'committed',
      render: (amount: number) => `$${amount.toLocaleString()}`,
    },
    {
      title: 'Available',
      dataIndex: 'available',
      key: 'available',
      render: (amount: number, record: any) => (
        <Text style={{ 
          color: amount < record.budget * 0.1 ? '#ff4d4f' : '#52c41a',
          fontWeight: 500 
        }}>
          ${amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Utilization',
      key: 'utilization',
      render: (record: any) => {
        const utilization = ((record.spent + record.committed) / record.budget) * 100;
        return (
          <Progress
            percent={utilization}
            size="small"
            status={utilization > 90 ? 'exception' : 'active'}
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Procurement Dashboard</Title>
          <Text type="secondary">Manage purchase requisitions, orders, and vendor relationships</Text>
        </div>
        <Space>
          <Select
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            style={{ width: 120 }}
          >
            <Select.Option value="week">This Week</Select.Option>
            <Select.Option value="month">This Month</Select.Option>
            <Select.Option value="quarter">This Quarter</Select.Option>
            <Select.Option value="year">This Year</Select.Option>
          </Select>
          <Button icon={<BellOutlined />}>
            Notifications
          </Button>
          <Button icon={<SettingOutlined />} onClick={onManageWorkflows}>
            Workflows
          </Button>
        </Space>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Spend"
              value={dashboardData?.totalSpend || 0}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Requisitions"
              value={dashboardData?.totalRequisitions || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Approvals"
              value={dashboardData?.pendingApprovals || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Vendors"
              value={dashboardData?.activeVendors || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Approval Time"
              value={dashboardData?.avgApprovalTime || 0}
              suffix="days"
              precision={1}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Cost Savings"
              value={dashboardData?.costSavings || 0}
              prefix="$"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="On-Time Delivery"
              value={dashboardData?.onTimeDelivery || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Budget Utilization</Text>
            </div>
            <Progress
              percent={dashboardData?.budgetUtilization || 0}
              status={dashboardData?.budgetUtilization > 90 ? 'exception' : 'active'}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card title="Quick Actions" style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateRequisition}>
            Create Requisition
          </Button>
          <Button icon={<ShoppingCartOutlined />} onClick={onCreatePurchaseOrder}>
            Create Purchase Order
          </Button>
          <Button icon={<UserOutlined />} onClick={onManageVendors}>
            Manage Vendors
          </Button>
          <Button icon={<CheckCircleOutlined />}>
            Approve Pending
          </Button>
          <Button icon={<TruckOutlined />}>
            Track Deliveries
          </Button>
          <Button icon={<DollarOutlined />}>
            Process Payments
          </Button>
          <Button icon={<ExportOutlined />}>
            Export Reports
          </Button>
        </Space>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Row gutter={[16, 16]}>
            {/* Recent Requisitions */}
            <Col xs={24} lg={12}>
              <Card title="Recent Requisitions" extra={<Button type="link">View All</Button>}>
                <Table
                  dataSource={dashboardData?.recentRequisitions || []}
                  columns={requisitionColumns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>

            {/* Recent Purchase Orders */}
            <Col xs={24} lg={12}>
              <Card title="Recent Purchase Orders" extra={<Button type="link">View All</Button>}>
                <List
                  dataSource={dashboardData?.recentPurchaseOrders || []}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<ShoppingCartOutlined />} />}
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{item.orderNumber}</span>
                            <Tag color={getStatusColor(item.status)}>
                              {item.status.toUpperCase()}
                            </Tag>
                          </div>
                        }
                        description={
                          <div>
                            <div>Vendor: {item.vendor}</div>
                            <div>Amount: ${item.totalAmount.toLocaleString()}</div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              Expected: {item.expectedDelivery.toLocaleDateString()}
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Pending Approvals" key="approvals">
          <Card>
            <Table
              dataSource={dashboardData?.pendingApprovalsList || []}
              columns={approvalColumns}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Vendor Performance" key="vendors">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Top Vendors by Spend">
                <List
                  dataSource={dashboardData?.topVendors || []}
                  renderItem={(vendor: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={vendor.name}
                        description={
                          <div>
                            <div>Total Spend: ${vendor.totalSpend.toLocaleString()}</div>
                            <div>Orders: {vendor.orderCount} | Rating: {vendor.rating}/5</div>
                            <Progress
                              percent={vendor.onTimeDelivery}
                              size="small"
                              format={() => `${vendor.onTimeDelivery}% On-Time`}
                            />
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Approval Metrics">
                <List
                  dataSource={dashboardData?.approvalMetrics || []}
                  renderItem={(metric: any) => (
                    <List.Item>
                      <List.Item.Meta
                        title={metric.step}
                        description={
                          <div>
                            <div>Avg Time: {metric.avgTime} days | Pending: {metric.pendingCount}</div>
                            <Progress
                              percent={metric.approvalRate}
                              size="small"
                              format={() => `${metric.approvalRate}% Approval Rate`}
                            />
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Budget Status" key="budget">
          <Card>
            <Table
              dataSource={dashboardData?.budgetStatus || []}
              columns={budgetColumns}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}; 